import { useState } from 'react'
import { authApi } from '../../lib/auth'

// The control that makes a 120-lesson course navigable.
//
// Optimistic, and it puts the tick back when the server disagrees. Marking a
// lesson done is not worth a spinner, and a silent failure here is worse than
// most: the reader carries on believing the course knows where they are, and
// finds out weeks later that it does not.

export default function LessonDoneButton({
  courseSlug,
  lessonSlug,
  initialDone,
}: {
  courseSlug: string
  lessonSlug: string
  initialDone: boolean
}) {
  const [done, setDone] = useState(initialDone)
  const [error, setError] = useState('')

  async function toggle() {
    const next = !done
    setDone(next)
    setError('')
    try {
      await authApi.setLessonProgress(courseSlug, lessonSlug, next)
    } catch (e) {
      // Back to what the server still believes, so the page never shows a tick
      // that was not saved.
      setDone(!next)
      setError((e as Error).message)
    }
  }

  return (
    <div className="mt-10 border-t border-border-subtle pt-6">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={done}
        className={`inline-flex items-center gap-2 rounded-smooth border-2 px-5 py-2.5 font-heading text-body-sm font-bold transition-colors ${
          done
            ? 'border-brand-navy bg-brand-navy text-white'
            : 'border-border-strong text-brand-navy hover:border-brand-navy'
        }`}
      >
        {/* A visible label, not an icon on its own: the state has to be readable
            without hovering anything. */}
        <span aria-hidden="true">{done ? '✓' : '○'}</span>
        {done ? 'Marked as done' : 'Mark as done'}
      </button>
      {error ? <p className="mt-2 font-body text-body-sm text-brand-red">{error}</p> : null}
    </div>
  )
}
